import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  HelpCircle, 
  Mail, 
  LogOut,
  Shield,
  ChevronRight,
  Plug,
  UserSearch,
  Megaphone,
  ScrollText,
  Settings as SettingsIcon,
  Wallet,
  FileText
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const { user, logout, isOwner, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    {
      label: 'Dashboard',
      path: '/admin/dashboard',
      icon: LayoutDashboard,
      roles: ['owner', 'admin', 'outreach']
    },
    {
      label: 'Leads',
      path: '/admin/leads',
      icon: UserSearch,
      roles: ['owner', 'admin']
    },
    {
      label: 'Campaigns',
      path: '/admin/campaigns',
      icon: Megaphone,
      roles: ['owner', 'admin']
    },
    {
      label: 'Payments',
      path: '/admin/payments',
      icon: Wallet,
      roles: ['owner', 'admin']
    },
    {
      label: 'CMS',
      path: '/admin/cms',
      icon: FileText,
      roles: ['owner', 'admin']
    },
    {
      label: 'Users',
      path: '/admin/users',
      icon: Users,
      roles: ['owner', 'admin']
    },
    {
      label: 'Integrations',
      path: '/admin/integrations',
      icon: Plug,
      roles: ['owner', 'admin']
    },
    {
      label: 'FAQs',
      path: '/admin/faqs',
      icon: HelpCircle,
      roles: ['owner', 'admin', 'outreach']
    },
    {
      label: 'Inquiries',
      path: '/admin/inquiries',
      icon: Mail,
      roles: ['owner', 'admin', 'outreach']
    },
    {
      label: 'Audit Logs',
      path: '/admin/audit-logs',
      icon: ScrollText,
      roles: ['owner', 'admin']
    },
    {
      label: 'Settings',
      path: '/admin/settings',
      icon: SettingsIcon,
      roles: ['owner', 'admin', 'outreach']
    }
  ];

  const filteredNavItems = navItems.filter(item => {
    if (!user) return false;
    return item.roles.includes(user.role);
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'admin':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'outreach':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      default:
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-800/50 border-r border-slate-700/50 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-slate-900" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Coinexus</h1>
              <p className="text-xs text-slate-400">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const Icon = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' 
                    : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-700/50">
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-amber-400 font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className={`px-2 py-1 text-xs font-medium rounded border ${getRoleBadgeColor(user?.role)}`}>
                {user?.role?.toUpperCase()}
              </span>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
